/**
 * Phone layout of the participants list: one card per person (the table needs ~12 columns).
 * Tapping the card opens the detail; payment and check-in controls work from the card.
 */

import { useEffect, useState } from 'react';
import { Search, ShieldCheck, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { formatPrice } from '@/models/event';
import type { ParticipantWithDetails } from '@/Services/eventParticipants.service';
import { CheckInButton } from './CheckInButton';
import { PaymentButton } from './PaymentButton';
import { StopRowClick } from './StopRowClick';

const PAGE = 20;

interface ParticipantCardListProps {
  eventId: string;
  participants: ParticipantWithDetails[];
  isLoading: boolean;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onOpen: (participant: ParticipantWithDetails) => void;
  onRequestPayment: (participant: ParticipantWithDetails) => void;
  onRequestReversal: (participant: ParticipantWithDetails) => void;
  canOperate: boolean;
  emptyMessage?: string;
}

const REGISTRATION_BADGE = {
  PRE_REGISTERED: <Badge variant="outline" className="border-amber-500 text-amber-700">Por revisar</Badge>,
  VERIFIED: <Badge variant="secondary">Verificada</Badge>,
  REJECTED: <Badge variant="destructive">Rechazada</Badge>,
} as const;

export function ParticipantCardList({
  eventId,
  participants,
  isLoading,
  searchValue,
  onSearchChange,
  onOpen,
  onRequestPayment,
  onRequestReversal,
  canOperate,
  emptyMessage = 'Sin participantes',
}: ParticipantCardListProps) {
  const [visible, setVisible] = useState(PAGE);
  const [text, setText] = useState(searchValue);

  // Debounce typing so each keystroke doesn't refetch
  useEffect(() => {
    const t = setTimeout(() => onSearchChange(text), 350);
    return () => clearTimeout(t);
  }, [text, onSearchChange]);

  // New result set → start from the top again
  useEffect(() => setVisible(PAGE), [participants]);

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Buscar por nombre, código o CI…"
          className="pl-9"
        />
      </div>

      {isLoading ? (
        Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-32" />)
      ) : participants.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">{emptyMessage}</p>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">{participants.length} participante(s)</p>
          {participants.slice(0, visible).map((p) => {
            const isMinister = !!p.minister_id;
            const name = isMinister ? p.minister?.full_name : p.guest?.full_name;
            const isDelegate = !!p.guest?.is_delegate;
            const representing =
              p.guest?.representing_minister?.full_name ?? p.guest?.represented_name ?? p.guest?.institution_note ?? null;
            const place = [p.minister?.district?.name, p.minister?.region?.name].filter(Boolean).join(' · ');
            const paid = p.payment_status === 'PAID';
            const price = paid ? p.payment_amount : p.calculated_price;

            return (
              <Card key={p.id} className="cursor-pointer transition-colors hover:bg-muted/50" onClick={() => onOpen(p)}>
                <CardContent className="space-y-2 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="flex items-center gap-1.5 font-medium">
                        <span className="truncate">{name}</span>
                        {p.is_mandatory && <ShieldCheck className="size-3.5 shrink-0 text-primary" aria-label="Obligatorio" />}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {[p.minister?.adb_code, place].filter(Boolean).join(' · ') || (isMinister ? '' : 'Sin código')}
                      </p>
                    </div>
                    {REGISTRATION_BADGE[p.registration_status]}
                  </div>

                  {!isMinister && (
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge variant={isDelegate ? 'default' : 'secondary'} className="text-xs">
                        {isDelegate ? 'Delegado' : 'Invitado'}
                      </Badge>
                      {isDelegate && (
                        <span className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
                          <Users className="size-3 shrink-0" />
                          <span className="truncate">Representa a {representing ?? 'otro ministro / institución'}</span>
                        </span>
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold">{formatPrice(Number(price ?? 0))}</span>
                    <StopRowClick>
                      <div className="flex items-center gap-1">
                        <PaymentButton
                          paymentStatus={p.payment_status as 'PENDING' | 'PARTIAL' | 'PAID' | 'REVERSED'}
                          onRequestPayment={() => onRequestPayment(p)}
                          onRequestReversal={() => onRequestReversal(p)}
                          disabled={!canOperate}
                        />
                        <CheckInButton
                          participantId={p.id}
                          attendanceStatus={p.attendance_status as 'PENDING' | 'ATTENDED' | 'ABSENT'}
                          participantName={name ?? 'Participante'}
                          eventId={eventId}
                          disabled={!canOperate}
                        />
                      </div>
                    </StopRowClick>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          {visible < participants.length && (
            <Button variant="outline" className="w-full" onClick={() => setVisible((v) => v + PAGE)}>
              Mostrar más ({participants.length - visible} restantes)
            </Button>
          )}
        </>
      )}
    </div>
  );
}
